import { useFiltersContext } from '@va/shared/feature-filters';
import { SeeMoreButton } from '@va/ui/components/buttons';
import { useReportBlockConfig } from '@va/ui/components/report-block';
import { storeFilters, storeSegmentIds } from '@va/util/helpers';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

const SCROLL_RETRY_INTERVAL_MS = 150;
const SCROLL_MAX_ATTEMPTS = 20; // ~3s — covers a lazy-loaded route chunk loading after navigation

/**
 * Scrolls the report block with the given DOM id into view. The target page may be lazy-loaded, so
 * we poll until the element exists rather than relying on a single fixed delay.
 * The DOM id is set on the report block container by `ReportBlockBody`.
 */
const scrollReportBlockIntoView = (reportBlockId: string, attemptsLeft = SCROLL_MAX_ATTEMPTS) => {
  const element = document.getElementById(reportBlockId);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  if (attemptsLeft > 0) {
    setTimeout(() => scrollReportBlockIntoView(reportBlockId, attemptsLeft - 1), SCROLL_RETRY_INTERVAL_MS);
  }
};

type ViewAllNavigationOptions = {
  route: string;
  targetReportBlockId: string;
  /** Optional side-effect to run right before navigating (e.g. dispatching extra UI state). */
  onBeforeNavigate?: () => void;
  /**
   * When true, scrolls the matching report block into view on the destination page after navigating.
   * Defaults to false.
   */
  scrollIntoView?: boolean;
};

/**
 * Returns a click handler that transfers the card's currently applied filters to the detailed report
 * block (identified by `targetReportBlockId`) before navigating, so the detail page opens with the
 * same filters applied. Always writes (even with no filters), so an empty card clears the detail page
 * back to its default state. When `scrollIntoView` is set, also scrolls the matching report block
 * into view after navigating.
 *
 * Must be called inside the card's `FiltersContextProvider` so it can read the applied filters.
 */
export const useViewAllNavigation = ({
  route,
  targetReportBlockId,
  onBeforeNavigate,
  scrollIntoView = false,
}: ViewAllNavigationOptions) => {
  const history = useHistory();
  const { appliedFilters } = useFiltersContext();
  const { storageNamespace } = useReportBlockConfig();

  return useCallback(() => {
    storeFilters(storageNamespace, targetReportBlockId, appliedFilters);
    storeSegmentIds(storageNamespace, targetReportBlockId, []);
    onBeforeNavigate?.();
    history.push(route);
    if (scrollIntoView) {
      scrollReportBlockIntoView(targetReportBlockId);
    }
  }, [appliedFilters, storageNamespace, targetReportBlockId, onBeforeNavigate, route, scrollIntoView, history]);
};

type ViewAllButtonProps = ViewAllNavigationOptions & {
  text: string;
};

export const ViewAllButton = ({
  text,
  route,
  targetReportBlockId,
  onBeforeNavigate,
  scrollIntoView = false,
}: ViewAllButtonProps) => {
  const onClick = useViewAllNavigation({ route, targetReportBlockId, onBeforeNavigate, scrollIntoView });

  return <SeeMoreButton text={text} onClick={onClick} />;
};
